<script setup lang="ts">
import type { ReceiptModel } from '~/utils/receipt-model/ReceiptModel'
import useDownloadReceipt from '~/store-v2/payment-model/useDownloadReceipt'

const { projectId, transfers } = defineProps<{
  projectId: string
  transfers: ReceiptModel['transfers'] | null
}>()

const { t } = useI18n()

// VKIT: the kit is paid straight to the distributor; the alert follows the `equipment` transfer.
const equipmentTransfer = computed(() => transfers?.find(transfer => transfer.category === 'equipment'))
const isPaid = computed(() => equipmentTransfer.value?.status === 'paid')

const { mutate: downloadReceipt, isPending: isDownloadingReceipt } = useDownloadReceipt(computed(() => projectId))

// SolAlert has no loading state for its action: the label changes and repeated clicks are ignored.
const receiptActionLabel = computed(() => (
  isDownloadingReceipt.value
    ? t('receipt-model.supplier_info.vkit_paid.downloading')
    : t('receipt-model.receivables.view_receipt')
))

function handleDownloadReceipt() {
  const transfer = equipmentTransfer.value
  if (!transfer || isDownloadingReceipt.value)
    return

  downloadReceipt(transfer)
}
</script>

<template>
  <SolAlert
    v-if="isPaid"
    id="vkit-kit-payment-paid"
    class="vkit-payment-alert"
    :class="{ 'vkit-payment-alert--busy': isDownloadingReceipt }"
    feedback="informative"
    :title="t('receipt-model.supplier_info.vkit_paid.title')"
    :action="receiptActionLabel"
    :aria-busy="isDownloadingReceipt || undefined"
    @click="handleDownloadReceipt"
  >
    {{ t('receipt-model.supplier_info.vkit_paid.text') }}
  </SolAlert>
  <SolAlert
    v-else
    id="vkit-kit-payment-scheduled"
    feedback="informative"
    :title="t('receipt-model.supplier_info.vkit_scheduled.title')"
  >
    {{ t('receipt-model.supplier_info.vkit_scheduled.text') }}
  </SolAlert>
</template>

<style lang="scss" scoped>
.vkit-payment-alert {
  @apply flex-col items-start gap-4xs;

  @screen md:site {
    @apply flex-row items-center gap-0;
  }

  &--busy :deep(.alert-button) {
    @apply pointer-events-none opacity-intense;
  }
}
</style>
